import React from 'react';
import { AppBar, Box, Toolbar } from '@mui/material';
import { AppsFolder } from '../AppsFolder';
import {
  MobileMenuDrawer,
  NavBarDesktopActions,
  NavBarLogo,
  NavBarMobileActions,
  NavBarShortcuts,
} from '../components';
import { NAVBAR_Z_INDEX } from '../constants';
import { useNavBarState } from '../hooks';
import { NavBarProps } from '../interfaces';

export const NavBarContainer = ({ variant = 'default' }: NavBarProps) => {
  const {
    isJobPosting,
    isMobileMenuOpen,
    isAppsOpen,
    openDropdown,
    formattedDate,
    userName,
    companyLogo,
    toggleMobileMenu,
    closeMobileMenu,
    setIsAppsOpen,
    setOpenDropdown,
  } = useNavBarState({ variant });

  return (
    <>
      <AppBar
        position='fixed'
        elevation={0}
        component='nav'
        aria-label='Main Navigation'
        sx={{
          zIndex: NAVBAR_Z_INDEX,
          height: 'var(--nav-height, 64px)',
          bgcolor: 'var(--navbar-bg, var(--brand-secondary, #093725))',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          backdropFilter: 'saturate(180%) blur(12px)',
          transition: 'all 0.2s ease',
          borderRadius: 0,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            height: '100%',
            minHeight: 'var(--nav-height, 64px) !important',
            px: { xs: 2, sm: 2.5, lg: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
          }}
        >
          {/* Left: When job posting, display Company Logo on left corner; otherwise display Navigation shortcuts */}
          {isJobPosting ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <NavBarLogo
                isJobPosting={isJobPosting}
                companyLogo={companyLogo}
              />
            </Box>
          ) : (
            <NavBarShortcuts
              isJobPosting={isJobPosting}
              isAppsOpen={isAppsOpen}
              onOpenApps={() => setIsAppsOpen(true)}
            />
          )}

          {/* Center: Horizontally centered Company Logo only for standard app navigation */}
          {!isJobPosting && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'auto',
                zIndex: 1,
              }}
            >
              <NavBarLogo
                isJobPosting={isJobPosting}
                companyLogo={companyLogo}
              />
            </Box>
          )}

          {/* Desktop Right: Welcome, Notifications, Locale, Profile */}
          <NavBarDesktopActions
            isJobPosting={isJobPosting}
            userName={userName}
            formattedDate={formattedDate}
          />

          {/* Mobile Right: Locale, Notifications, Settings, Menu Toggle */}
          <NavBarMobileActions
            isJobPosting={isJobPosting}
            isMobileMenuOpen={isMobileMenuOpen}
            onToggleMobileMenu={toggleMobileMenu}
          />
        </Toolbar>
      </AppBar>

      {/* Mobile Responsive Navigation Drawer */}
      {!isJobPosting && (
        <MobileMenuDrawer
          isOpen={isMobileMenuOpen}
          onClose={closeMobileMenu}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
        />
      )}

      {/* iOS App Folder Launchpad */}
      {!isJobPosting && (
        <AppsFolder
          isOpen={isAppsOpen}
          onClose={() => setIsAppsOpen(false)}
        />
      )}
    </>
  );
};
